import { useEffect, useMemo, useState } from "react";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Trash2,
  Eye,
  EyeOff,
  Package,
  Layers,
  Image as ImageIcon,
  BarChart3,
  ShoppingBag,
  Users,
  CheckCircle2,
  Pencil,
  UserRound,
  Search,
  Download,
  Ban,
  ShieldCheck,
  Settings as SettingsIcon,
  Tag,
  Star,
  Film,
  Save,
} from "lucide-react";
import Logo from "@/components/Logo";
import ImageUploader from "@/components/ImageUploader";

const SIZE_ALL = ["S", "M", "L", "XL", "XXL"];

/* ==================== Root ==================== */
export default function Admin() {
  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16" data-testid="admin-page">
      <div className="flex items-center gap-4 mb-6">
        <Logo variant="monogram" className="h-12 w-12 dark:invert" loading="eager" />
        <div>
          <p className="label-xs opacity-60">Admin Panel</p>
          <h1 className="text-5xl font-serif tracking-tighter leading-none">Studio</h1>
        </div>
      </div>

      <Tabs defaultValue="dashboard" className="mt-10">
        <TabsList className="rounded-none flex-wrap h-auto">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard" className="rounded-none label-xs gap-2">
            <BarChart3 className="h-3.5 w-3.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products" className="rounded-none label-xs gap-2">
            <Package className="h-3.5 w-3.5" /> Products
          </TabsTrigger>
          <TabsTrigger value="collections" data-testid="tab-collections" className="rounded-none label-xs gap-2">
            <Layers className="h-3.5 w-3.5" /> Collections
          </TabsTrigger>
          <TabsTrigger value="banners" data-testid="tab-banners" className="rounded-none label-xs gap-2">
            <ImageIcon className="h-3.5 w-3.5" /> Hero Banners
          </TabsTrigger>
          <TabsTrigger value="homepage" data-testid="tab-homepage" className="rounded-none label-xs gap-2">
            <Film className="h-3.5 w-3.5" /> Homepage Media
          </TabsTrigger>
          <TabsTrigger value="coupons" data-testid="tab-coupons" className="rounded-none label-xs gap-2">
            <Tag className="h-3.5 w-3.5" /> Coupons
          </TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews" className="rounded-none label-xs gap-2">
            <Star className="h-3.5 w-3.5" /> Reviews
          </TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders" className="rounded-none label-xs gap-2">
            <ShoppingBag className="h-3.5 w-3.5" /> Orders
          </TabsTrigger>
          <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-none label-xs gap-2">
            <UserRound className="h-3.5 w-3.5" /> Customers
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-none label-xs gap-2">
            <SettingsIcon className="h-3.5 w-3.5" /> Settings
          </TabsTrigger>
          <TabsTrigger value="founders" data-testid="tab-founders" className="rounded-none label-xs gap-2">
            <Users className="h-3.5 w-3.5" /> Founders
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-8">
          <DashboardTab />
        </TabsContent>
        <TabsContent value="products" className="mt-8">
          <ProductsTab />
        </TabsContent>
        <TabsContent value="collections" className="mt-8">
          <CollectionsTab />
        </TabsContent>
        <TabsContent value="banners" className="mt-8">
          <BannersTab />
        </TabsContent>
        <TabsContent value="homepage" className="mt-8">
          <HomepageMediaTab />
        </TabsContent>
        <TabsContent value="coupons" className="mt-8">
          <CouponsTab />
        </TabsContent>
        <TabsContent value="reviews" className="mt-8">
          <ReviewsTab />
        </TabsContent>
        <TabsContent value="orders" className="mt-8">
          <OrdersTab />
        </TabsContent>
        <TabsContent value="customers" className="mt-8">
          <CustomersTab />
        </TabsContent>
        <TabsContent value="settings" className="mt-8">
          <SettingsTab />
        </TabsContent>
        <TabsContent value="founders" className="mt-8">
          <FoundersTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ==================== Dashboard ==================== */
function DashboardTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  const cards = [
    { label: "Products", value: stats?.total_products, sub: `${stats?.active_products ?? 0} active` },
    { label: "Collections", value: stats?.total_collections, sub: `${stats?.visible_collections ?? 0} visible` },
    { label: "Low Stock", value: stats?.low_stock_products, sub: "≤ 3 units per size", highlight: (stats?.low_stock_products ?? 0) > 0 },
    { label: "Out of Stock", value: stats?.out_of_stock_products, sub: "Zero across all sizes", highlight: (stats?.out_of_stock_products ?? 0) > 0 },
    { label: "Orders", value: stats?.total_orders, sub: `${stats?.pending_orders ?? 0} to confirm` },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="dashboard-tab">
      {cards.map((c) => (
        <div key={c.label} className="border p-6" data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <p className="label-xs opacity-60">{c.label}</p>
          <p className={`mt-3 text-5xl font-serif tracking-tighter ${c.highlight ? "" : ""}`}>
            {c.value ?? "—"}
          </p>
          <p className="mt-2 text-xs opacity-60">{c.sub}</p>
        </div>
      ))}
    </div>
  );
}

/* ==================== Products ==================== */
const EMPTY_PRODUCT = {
  name: "",
  description: "",
  price: 899,
  discount_price: "",
  category: "",
  collection_id: "",
  sku: "",
  is_active: true,
  images: [],
  sizes: [...SIZE_ALL],
  size_stock: SIZE_ALL.reduce((a, s) => ({ ...a, [s]: 10 }), {}),
};

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [collections, setCollections] = useState([]);
  const [editing, setEditing] = useState(null); // null = new, or product object

  const load = () => {
    api.get("/products-v2?active_only=false").then(({ data }) => setProducts(data || []));
    api.get("/collections?all=true").then(({ data }) => setCollections(data || []));
  };
  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-8" data-testid="products-tab">
      <ProductForm
        key={editing?.id || "new"}
        initial={editing || EMPTY_PRODUCT}
        editing={!!editing}
        collections={collections}
        onDone={() => {
          setEditing(null);
          load();
        }}
        onCancel={() => setEditing(null)}
      />
      <div>
        <p className="label-xs opacity-60 mb-4">{products.length} product(s)</p>
        {products.length === 0 && (
          <div className="border border-dashed p-10 text-center">
            <p className="opacity-70">No products yet. Add your first product on the left.</p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {products.map((p) => {
            const inactive = p.is_active === false;
            const out = Number(p.stock ?? 0) === 0;
            return (
              <div key={p.id} className="border p-3 relative" data-testid={`admin-product-${p.id}`}>
                <div className="relative aspect-[4/5] bg-muted overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover" />
                  {inactive && (
                    <span className="absolute top-2 left-2 bg-background/90 border px-2 py-1 label-xs">
                      HIDDEN
                    </span>
                  )}
                  {out && (
                    <span className="absolute top-2 right-2 bg-foreground text-background px-2 py-1 label-xs">
                      OUT
                    </span>
                  )}
                </div>
                <div className="mt-3 flex justify-between items-start gap-2">
                  <div className="min-w-0">
                    <p className="font-serif truncate">{p.name}</p>
                    <p className="text-xs opacity-60 truncate">
                      {p.category || "—"} · {money(p.discount_price || p.price)}
                    </p>
                    <p className="text-xs opacity-60 mt-1">Stock: {p.stock}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => setEditing(p)}
                      aria-label="Edit"
                      data-testid={`admin-edit-${p.id}`}
                      className="h-8 w-8 grid place-items-center border hover:bg-foreground hover:text-background"
                      style={{ transitionProperty: "background-color, color" }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => del(p.id)}
                      aria-label="Delete"
                      data-testid={`admin-del-${p.id}`}
                      className="h-8 w-8 grid place-items-center border hover:bg-foreground hover:text-background"
                      style={{ transitionProperty: "background-color, color" }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ProductForm({ initial, editing, collections, onDone, onCancel }) {
  const [form, setForm] = useState({
    ...EMPTY_PRODUCT,
    ...initial,
    discount_price: initial.discount_price ?? "",
    size_stock: SIZE_ALL.reduce(
      (a, s) => ({ ...a, [s]: Number(initial.size_stock?.[s] ?? 0) }),
      {}
    ),
    images: Array.isArray(initial.images) ? initial.images : initial.image ? [initial.image] : [],
  });
  const [saving, setSaving] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const setStock = (s, v) =>
    setForm((f) => ({ ...f, size_stock: { ...f.size_stock, [s]: Math.max(0, Number(v || 0)) } }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Product name required");
    if (!form.images.length) return toast.error("Add at least one image");
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        price: Number(form.price),
        discount_price: form.discount_price ? Number(form.discount_price) : null,
        category: form.category,
        collection_id: form.collection_id || null,
        images: form.images,
        sizes: SIZE_ALL,
        size_stock: form.size_stock,
        sku: form.sku,
        is_active: !!form.is_active,
      };
      if (editing) {
        await api.patch(`/admin/products-v2/${initial.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/admin/products-v2", payload);
        toast.success("Product added");
      }
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="border p-6 space-y-4 lg:sticky lg:top-24 h-fit"
      data-testid="product-form"
    >
      <div className="flex justify-between items-center">
        <p className="label-xs">{editing ? "Edit Product" : "New Product"}</p>
        {editing && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs opacity-60 hover:opacity-100 link-underline"
            data-testid="product-form-cancel"
          >
            Cancel
          </button>
        )}
      </div>

      <div>
        <Label className="label-xs">Images</Label>
        <div className="mt-2">
          <ImageUploader
            multiple
            kind="product"
            value={form.images}
            onChange={(imgs) => set({ images: imgs })}
            testid="product-images"
            label="Add Image"
          />
        </div>
      </div>

      <div>
        <Label className="label-xs">Name</Label>
        <Input
          required
          className="rounded-none mt-2"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          data-testid="pf-name"
        />
      </div>

      <div>
        <Label className="label-xs">Description</Label>
        <Textarea
          rows={3}
          className="rounded-none mt-2"
          value={form.description}
          onChange={(e) => set({ description: e.target.value })}
          data-testid="pf-desc"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-xs">Price (₹)</Label>
          <Input
            required
            type="number"
            className="rounded-none mt-2"
            value={form.price}
            onChange={(e) => set({ price: e.target.value })}
            data-testid="pf-price"
          />
        </div>
        <div>
          <Label className="label-xs">Discount Price (optional)</Label>
          <Input
            type="number"
            className="rounded-none mt-2"
            value={form.discount_price}
            onChange={(e) => set({ discount_price: e.target.value })}
            data-testid="pf-discount"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="label-xs">Category</Label>
          <Input
            className="rounded-none mt-2"
            value={form.category}
            onChange={(e) => set({ category: e.target.value })}
            data-testid="pf-category"
          />
        </div>
        <div>
          <Label className="label-xs">SKU</Label>
          <Input
            className="rounded-none mt-2"
            value={form.sku}
            onChange={(e) => set({ sku: e.target.value })}
            data-testid="pf-sku"
          />
        </div>
      </div>

      <div>
        <Label className="label-xs">Collection</Label>
        <Select
          value={form.collection_id || "__none__"}
          onValueChange={(v) => set({ collection_id: v === "__none__" ? "" : v })}
        >
          <SelectTrigger className="rounded-none mt-2" data-testid="pf-collection">
            <SelectValue placeholder="No collection" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">No collection</SelectItem>
            {collections.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="label-xs">Stock per size</Label>
        <div className="mt-2 grid grid-cols-5 gap-2">
          {SIZE_ALL.map((s) => (
            <div key={s} className="flex flex-col items-center">
              <span className="label-xs opacity-60 mb-1">{s}</span>
              <Input
                type="number"
                min="0"
                className="rounded-none text-center h-10"
                value={form.size_stock[s] ?? 0}
                onChange={(e) => setStock(s, e.target.value)}
                data-testid={`pf-stock-${s}`}
              />
            </div>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-3 select-none">
        <input
          type="checkbox"
          checked={!!form.is_active}
          onChange={(e) => set({ is_active: e.target.checked })}
          className="h-4 w-4 accent-foreground"
          data-testid="pf-active"
        />
        <span className="text-sm">Published (visible on storefront)</span>
      </label>

      <Button
        type="submit"
        disabled={saving}
        className="w-full rounded-none label-xs h-11"
        data-testid="pf-submit"
      >
        {saving ? "Saving…" : editing ? "Save Changes" : "Add Product"}
      </Button>
    </form>
  );
}

/* ==================== Collections ==================== */
function CollectionsTab() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [heroImage, setHeroImage] = useState("");

  const load = () => api.get("/admin/collections").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    try {
      await api.post("/admin/collections", {
        name,
        description,
        hero_image: heroImage,
        order: rows.length + 1,
      });
      toast.success("Collection created");
      setName("");
      setDescription("");
      setHeroImage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const patch = async (id, body) => {
    await api.patch(`/admin/collections/${id}`, body);
    load();
  };

  const del = async (id) => {
    if (!confirm("Delete collection? Products will be un-linked but not removed.")) return;
    await api.delete(`/admin/collections/${id}`);
    toast.success("Deleted");
    load();
  };

  const move = async (id, dir) => {
    const idx = rows.findIndex((r) => r.id === id);
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    await Promise.all([
      api.patch(`/admin/collections/${rows[idx].id}`, { order: rows[j].order }),
      api.patch(`/admin/collections/${rows[j].id}`, { order: rows[idx].order }),
    ]);
    load();
  };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8" data-testid="collections-tab">
      <form onSubmit={create} className="border p-6 space-y-4 h-fit" data-testid="collection-form">
        <p className="label-xs">New Collection</p>
        <div>
          <Label className="label-xs">Hero image (optional)</Label>
          <div className="mt-2">
            <ImageUploader
              value={heroImage}
              onChange={setHeroImage}
              kind="collection"
              aspect="aspect-[3/2]"
              testid="collection-hero"
              label="Add Hero"
            />
          </div>
        </div>
        <div>
          <Label className="label-xs">Name</Label>
          <Input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="rounded-none mt-2"
            data-testid="cf-name"
          />
        </div>
        <div>
          <Label className="label-xs">Description</Label>
          <Textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="rounded-none mt-2"
            data-testid="cf-desc"
          />
        </div>
        <Button type="submit" className="w-full rounded-none label-xs h-11" data-testid="cf-submit">
          Create Collection
        </Button>
      </form>

      <div className="space-y-3">
        {rows.length === 0 && (
          <div className="border border-dashed p-10 text-center opacity-70">No collections yet.</div>
        )}
        {rows.map((c, i) => (
          <CollectionRow
            key={c.id}
            row={c}
            first={i === 0}
            last={i === rows.length - 1}
            onPatch={patch}
            onDelete={del}
            onMove={move}
          />
        ))}
      </div>
    </div>
  );
}

function CollectionRow({ row, first, last, onPatch, onDelete, onMove }) {
  const [name, setName] = useState(row.name);
  const [desc, setDesc] = useState(row.description);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setName(row.name);
    setDesc(row.description);
    setDirty(false);
  }, [row.id, row.name, row.description]);

  return (
    <div className="border p-4 flex flex-wrap items-center gap-4" data-testid={`col-row-${row.id}`}>
      <div className="w-16 aspect-[3/2] bg-muted overflow-hidden">
        {row.hero_image ? <img src={row.hero_image} alt="" className="w-full h-full object-cover" /> : null}
      </div>
      <div className="flex-1 min-w-[200px]">
        <Input
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setDirty(true);
          }}
          className="rounded-none"
          data-testid={`col-name-${row.id}`}
        />
        <Textarea
          rows={2}
          value={desc || ""}
          onChange={(e) => {
            setDesc(e.target.value);
            setDirty(true);
          }}
          className="rounded-none mt-2 text-xs"
          data-testid={`col-desc-${row.id}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <button
          onClick={() => onMove(row.id, -1)}
          disabled={first}
          className="h-8 w-8 border grid place-items-center disabled:opacity-30"
          aria-label="Move up"
          data-testid={`col-up-${row.id}`}
        >
          ↑
        </button>
        <button
          onClick={() => onMove(row.id, 1)}
          disabled={last}
          className="h-8 w-8 border grid place-items-center disabled:opacity-30"
          aria-label="Move down"
          data-testid={`col-down-${row.id}`}
        >
          ↓
        </button>
      </div>
      <div className="flex gap-2">
        <button
          onClick={() => onPatch(row.id, { is_visible: !row.is_visible })}
          className="h-9 px-3 border grid place-items-center label-xs"
          data-testid={`col-visible-${row.id}`}
        >
          {row.is_visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
        </button>
        <Button
          type="button"
          disabled={!dirty}
          onClick={() => onPatch(row.id, { name, description: desc }).then(() => setDirty(false))}
          className="rounded-none label-xs h-9 px-4"
          data-testid={`col-save-${row.id}`}
        >
          Save
        </Button>
        <button
          onClick={() => onDelete(row.id)}
          className="h-9 w-9 border grid place-items-center"
          aria-label="Delete"
          data-testid={`col-del-${row.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ==================== Hero Banners ==================== */
const EMPTY_BANNER = {
  eyebrow: "",
  title: "",
  subtitle: "",
  cta_label: "",
  cta_link: "",
  desktop_image: "",
  mobile_image: "",
  desktop_video: "",
  mobile_video: "",
  start_at: "",
  end_at: "",
  is_active: true,
  order: 1,
};

function BannersTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_BANNER);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const load = () => api.get("/admin/hero-banners").then(({ data }) => setRows(data || []));
  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.desktop_image && !form.desktop_video) return toast.error("Add either a desktop image or a desktop video URL");
    try {
      const payload = {
        ...form,
        start_at: form.start_at ? new Date(form.start_at).toISOString() : null,
        end_at: form.end_at ? new Date(form.end_at).toISOString() : null,
        order: Number(form.order) || rows.length + 1,
      };
      await api.post("/admin/hero-banners", payload);
      toast.success("Banner added");
      setForm(EMPTY_BANNER);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const patch = async (id, body) => {
    await api.patch(`/admin/hero-banners/${id}`, body);
    load();
  };
  const del = async (id) => {
    if (!confirm("Delete this banner?")) return;
    await api.delete(`/admin/hero-banners/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-8" data-testid="banners-tab">
      <form onSubmit={create} className="border p-6 space-y-4 h-fit" data-testid="banner-form">
        <p className="label-xs">New Hero Banner</p>
        <div>
          <Label className="label-xs">Desktop Image (16:9 or wider)</Label>
          <div className="mt-2">
            <ImageUploader
              value={form.desktop_image}
              onChange={(v) => set({ desktop_image: v })}
              kind="banner-desktop"
              aspect="aspect-[16/9]"
              testid="banner-desktop"
            />
          </div>
        </div>
        <div>
          <Label className="label-xs">Mobile Image (portrait recommended)</Label>
          <div className="mt-2">
            <ImageUploader
              value={form.mobile_image}
              onChange={(v) => set({ mobile_image: v })}
              kind="banner-mobile"
              aspect="aspect-[9/16]"
              testid="banner-mobile"
            />
          </div>
        </div>
        <div>
          <Label className="label-xs">Desktop Video URL (mp4 · optional — plays autoplay muted looped)</Label>
          <Input
            className="rounded-none mt-2"
            value={form.desktop_video}
            onChange={(e) => set({ desktop_video: e.target.value })}
            placeholder="https://…/hero.mp4"
            data-testid="bf-desktop-video"
          />
        </div>
        <div>
          <Label className="label-xs">Mobile Video URL (optional — falls back to desktop video)</Label>
          <Input
            className="rounded-none mt-2"
            value={form.mobile_video}
            onChange={(e) => set({ mobile_video: e.target.value })}
            placeholder="https://…/hero-mobile.mp4"
            data-testid="bf-mobile-video"
          />
        </div>
        <div>
          <Label className="label-xs">Eyebrow (small line above title)</Label>
          <Input
            className="rounded-none mt-2"
            value={form.eyebrow}
            onChange={(e) => set({ eyebrow: e.target.value })}
            data-testid="bf-eyebrow"
          />
        </div>
        <div>
          <Label className="label-xs">Title</Label>
          <Input
            className="rounded-none mt-2"
            value={form.title}
            onChange={(e) => set({ title: e.target.value })}
            data-testid="bf-title"
          />
        </div>
        <div>
          <Label className="label-xs">Subtitle</Label>
          <Textarea
            rows={2}
            className="rounded-none mt-2"
            value={form.subtitle}
            onChange={(e) => set({ subtitle: e.target.value })}
            data-testid="bf-subtitle"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-xs">CTA Label</Label>
            <Input
              className="rounded-none mt-2"
              value={form.cta_label}
              onChange={(e) => set({ cta_label: e.target.value })}
              data-testid="bf-cta-label"
            />
          </div>
          <div>
            <Label className="label-xs">CTA Link</Label>
            <Input
              placeholder="/shop or https://…"
              className="rounded-none mt-2"
              value={form.cta_link}
              onChange={(e) => set({ cta_link: e.target.value })}
              data-testid="bf-cta-link"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-xs">Start (optional)</Label>
            <Input
              type="datetime-local"
              className="rounded-none mt-2"
              value={form.start_at}
              onChange={(e) => set({ start_at: e.target.value })}
              data-testid="bf-start"
            />
          </div>
          <div>
            <Label className="label-xs">End (optional)</Label>
            <Input
              type="datetime-local"
              className="rounded-none mt-2"
              value={form.end_at}
              onChange={(e) => set({ end_at: e.target.value })}
              data-testid="bf-end"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-xs">Display order</Label>
            <Input
              type="number"
              className="rounded-none mt-2"
              value={form.order}
              onChange={(e) => set({ order: e.target.value })}
              data-testid="bf-order"
            />
          </div>
          <label className="flex items-end gap-3 select-none pb-1">
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => set({ is_active: e.target.checked })}
              className="h-4 w-4 accent-foreground"
              data-testid="bf-active"
            />
            <span className="text-sm">Active</span>
          </label>
        </div>
        <Button type="submit" className="w-full rounded-none label-xs h-11" data-testid="bf-submit">
          Save Banner
        </Button>
      </form>

      <div className="space-y-4">
        {rows.length === 0 && (
          <div className="border border-dashed p-10 text-center opacity-70">
            No banners yet. When you add one, it appears on the homepage automatically.
          </div>
        )}
        {rows.map((b) => (
          <div key={b.id} className="border p-4 flex flex-wrap gap-4 items-center" data-testid={`banner-row-${b.id}`}>
            <div className="w-40 aspect-[16/9] bg-muted overflow-hidden">
              {b.desktop_image && <img src={b.desktop_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="font-serif text-lg">{b.title || <span className="opacity-40">Untitled</span>}</p>
              <p className="text-xs opacity-60 mt-1">
                {b.eyebrow ? `${b.eyebrow} · ` : ""}
                Order {b.order} · {b.is_active ? "Active" : "Inactive"}
              </p>
              {(b.start_at || b.end_at) && (
                <p className="text-xs opacity-60 mt-1">
                  {b.start_at ? `From ${new Date(b.start_at).toLocaleString()}` : "From now"}
                  {" · "}
                  {b.end_at ? `Until ${new Date(b.end_at).toLocaleString()}` : "No end"}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => patch(b.id, { is_active: !b.is_active })}
                className="h-9 px-3 border label-xs flex items-center gap-2"
                data-testid={`banner-toggle-${b.id}`}
              >
                {b.is_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {b.is_active ? "Hide" : "Show"}
              </button>
              <button
                onClick={() => del(b.id)}
                className="h-9 w-9 border grid place-items-center"
                aria-label="Delete banner"
                data-testid={`banner-del-${b.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================== Orders ==================== */
const ORDER_STATUSES = [
  "pending", "confirmed", "packed", "shipped", "out_for_delivery",
  "delivered", "cancelled", "returned", "refunded",
];

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [payFilter, setPayFilter] = useState("");

  const load = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (statusFilter) p.set("status", statusFilter);
    if (payFilter) p.set("payment_status", payFilter);
    api.get(`/admin/orders/search?${p.toString()}`).then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, payFilter]);

  const changeStatus = async (o, status) => {
    await api.patch(`/admin/orders/${o.id}/status`, { status });
    toast.success(`Marked ${status}`);
    load();
  };
  const markFullyPaid = async (o) => {
    await api.post(`/admin/orders/${o.id}/mark-fully-paid`);
    toast.success("Marked fully paid");
    load();
  };

  return (
    <div className="space-y-4" data-testid="orders-tab">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
          <Input
            placeholder="Search order no, name, email or phone…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-10 rounded-none"
            data-testid="orders-search"
          />
        </div>
        <Select value={statusFilter || "__all__"} onValueChange={(v) => setStatusFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-none" data-testid="orders-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All statuses</SelectItem>
            {ORDER_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={payFilter || "__all__"} onValueChange={(v) => setPayFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-44 rounded-none" data-testid="orders-payment-filter">
            <SelectValue placeholder="Any payment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Any payment</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="advance_paid">Advance paid</SelectItem>
            <SelectItem value="fully_paid">Fully paid</SelectItem>
            <SelectItem value="refunded">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {orders.length === 0 && <p className="opacity-60 py-16 text-center">No orders found.</p>}
      {orders.map((o) => (
        <div key={o.id} className="border p-5 grid gap-4 lg:grid-cols-[1fr_auto] items-center" data-testid={`admin-order-${o.order_no}`}>
          <div>
            <p className="font-serif text-lg">{o.order_no}</p>
            <p className="text-xs opacity-60 mt-0.5">
              {new Date(o.created_at).toLocaleString()} · {(o.items || []).length} items · {o.shipping_address?.full_name || o.contact?.email || "—"}
            </p>
            <p className="text-xs opacity-60 mt-0.5">
              Payment: <span className="text-foreground">{o.payment_status}</span>{o.stock_decremented ? " · stock adjusted" : ""}
            </p>
            <p className="text-sm mt-2">
              {money(o.total)} · advance {money(o.advance_amount || 0)} / remaining {money(o.remaining_amount || 0)}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <a
              href={`${api.defaults.baseURL}/orders/${o.order_no}/invoice.pdf`}
              target="_blank"
              rel="noreferrer"
              className="h-9 px-3 border grid place-items-center label-xs gap-2 inline-flex hover:bg-foreground hover:text-background"
              style={{ transitionProperty: "background-color, color" }}
              data-testid={`admin-order-invoice-${o.order_no}`}
            >
              <Download className="h-3.5 w-3.5" /> PDF
            </a>
            {o.payment_status !== "fully_paid" && o.advance_payment_status === "paid" && (
              <Button
                onClick={() => markFullyPaid(o)}
                variant="outline"
                className="rounded-none border-foreground label-xs h-9 px-3"
                data-testid={`admin-order-fullypaid-${o.order_no}`}
              >
                Mark fully paid
              </Button>
            )}
            <Select value={o.status} onValueChange={(v) => changeStatus(o, v)}>
              <SelectTrigger className="w-44 rounded-none" data-testid={`admin-status-${o.order_no}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ORDER_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ==================== Founders ==================== */
function FoundersTab() {
  const [founders, setFounders] = useState([]);
  const load = () => api.get("/founders").then(({ data }) => setFounders(data));
  useEffect(() => {
    load();
  }, []);
  const save = async (f, patch) => {
    try {
      await api.patch(`/admin/founders/${f.id}`, patch);
      toast.success(`${f.name} updated`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  return (
    <div data-testid="founders-tab">
      <p className="label-xs opacity-60 mb-6">
        Upload real founder photos here. Only uploaded portraits appear on the About page — placeholder / stock URLs are ignored.
      </p>
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {founders.map((f) => (
          <FounderEditor key={f.id} founder={f} onSave={save} />
        ))}
      </div>
    </div>
  );
}

function FounderEditor({ founder, onSave }) {
  const [name, setName] = useState(founder.name);
  const [role, setRole] = useState(founder.role);
  const [photo, setPhoto] = useState(founder.photo);
  const dirty = name !== founder.name || role !== founder.role || photo !== founder.photo;
  return (
    <div className="border p-4" data-testid={`admin-founder-${founder.id}`}>
      <div className="mb-3">
        <ImageUploader
          value={photo}
          onChange={setPhoto}
          kind="founder"
          aspect="aspect-[4/5]"
          testid={`founder-photo-uploader-${founder.id}`}
          label="Upload Portrait"
        />
      </div>
      <Label className="label-xs">Name</Label>
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="rounded-none mt-1 mb-3"
        data-testid={`founder-name-${founder.id}`}
      />
      <Label className="label-xs">Role</Label>
      <Input
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="rounded-none mt-1 mb-3"
        data-testid={`founder-role-${founder.id}`}
      />
      <Button
        disabled={!dirty}
        onClick={() => onSave(founder, { name, role, photo })}
        className="w-full rounded-none label-xs h-10"
        data-testid={`founder-save-${founder.id}`}
      >
        {dirty ? "Save Changes" : "Saved"}
      </Button>
    </div>
  );
}

/* ==================== Customers ==================== */
function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [blockedFilter, setBlockedFilter] = useState("all"); // all | blocked | active
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const pageSize = 25;

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (blockedFilter === "blocked") params.set("blocked", "true");
      if (blockedFilter === "active") params.set("blocked", "false");
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data } = await api.get(`/admin/customers?${params.toString()}`);
      setRows(data.customers || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, blockedFilter, page]);

  const block = async (u) => {
    const reason = window.prompt(`Block ${u.name}? Optional reason:`, "");
    if (reason === null) return;
    await api.post(`/admin/customers/${u.user_id}/block`, { reason });
    toast.success(`${u.name} blocked`);
    load();
  };
  const unblock = async (u) => {
    await api.post(`/admin/customers/${u.user_id}/unblock`);
    toast.success(`${u.name} unblocked`);
    load();
  };
  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("tf_token") || "";
      const res = await fetch(`${api.defaults.baseURL}/admin/customers/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `thefabrics-customers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const openDetail = async (uid) => {
    try {
      const { data } = await api.get(`/admin/customers/${uid}`);
      setDetail(data);
    } catch (e) {
      toast.error("Failed to load customer");
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div data-testid="customers-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center justify-between mb-6">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 opacity-60" />
            <Input
              placeholder="Search name, email, phone…"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
              className="pl-10 rounded-none border-foreground/20 w-80 max-w-full"
              data-testid="customers-search"
            />
          </div>
          <Select
            value={blockedFilter}
            onValueChange={(v) => {
              setPage(1);
              setBlockedFilter(v);
            }}
          >
            <SelectTrigger className="w-40 rounded-none" data-testid="customers-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All customers</SelectItem>
              <SelectItem value="active">Active only</SelectItem>
              <SelectItem value="blocked">Blocked only</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3">
          <p className="label-xs opacity-60">
            {total} total · page {page} of {totalPages}
          </p>
          <Button onClick={exportCsv} className="rounded-none label-xs h-10 px-4 gap-2" data-testid="customers-export">
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="border overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="border-b bg-muted/40">
            <tr className="text-left">
              <Th>Customer</Th>
              <Th>Contact</Th>
              <Th>Orders</Th>
              <Th>Spent</Th>
              <Th>Verified</Th>
              <Th>Status</Th>
              <Th>Joined</Th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="p-6 text-center opacity-60">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-10 text-center opacity-60">
                  No customers found.
                </td>
              </tr>
            )}
            {rows.map((u) => (
              <tr key={u.user_id} className="border-t align-top" data-testid={`customer-row-${u.user_id}`}>
                <td className="p-3">
                  <button
                    className="font-serif text-base hover:underline text-left"
                    onClick={() => openDetail(u.user_id)}
                    data-testid={`customer-open-${u.user_id}`}
                  >
                    {u.name || "—"}
                  </button>
                  <p className="text-xs opacity-50 mt-0.5">{u.user_id}</p>
                </td>
                <td className="p-3">
                  <p className="truncate max-w-[200px]">{u.email}</p>
                  <p className="text-xs opacity-60 mt-0.5">{u.phone || "—"}</p>
                </td>
                <td className="p-3 tabular-nums">{u.orders_count}</td>
                <td className="p-3 tabular-nums">{money(u.total_spent)}</td>
                <td className="p-3">
                  {u.email_verified ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <span className="text-xs opacity-60">No</span>
                  )}
                </td>
                <td className="p-3">
                  {u.is_blocked ? (
                    <span className="label-xs border px-2 py-1 bg-foreground text-background">Blocked</span>
                  ) : (
                    <span className="label-xs border px-2 py-1">Active</span>
                  )}
                </td>
                <td className="p-3 text-xs opacity-70 whitespace-nowrap">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  {u.is_blocked ? (
                    <button
                      onClick={() => unblock(u)}
                      className="border h-8 px-3 label-xs inline-flex items-center gap-2 hover:bg-foreground hover:text-background"
                      style={{ transitionProperty: "background-color, color" }}
                      data-testid={`customer-unblock-${u.user_id}`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5" />
                      Unblock
                    </button>
                  ) : (
                    <button
                      onClick={() => block(u)}
                      className="border h-8 px-3 label-xs inline-flex items-center gap-2 hover:bg-foreground hover:text-background"
                      style={{ transitionProperty: "background-color, color" }}
                      data-testid={`customer-block-${u.user_id}`}
                    >
                      <Ban className="h-3.5 w-3.5" />
                      Block
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="border h-9 px-4 label-xs disabled:opacity-40"
            data-testid="customers-prev"
          >
            ← Prev
          </button>
          <span className="label-xs opacity-70">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="border h-9 px-4 label-xs disabled:opacity-40"
            data-testid="customers-next"
          >
            Next →
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {detail && <CustomerDetail data={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function Th({ children }) {
  return <th className="p-3 label-xs opacity-70 font-normal">{children}</th>;
}

function CustomerDetail({ data, onClose, onChanged }) {
  const block = async () => {
    const reason = window.prompt(`Block ${data.name}? Optional reason:`, data.blocked_reason || "");
    if (reason === null) return;
    await api.post(`/admin/customers/${data.user_id}/block`, { reason });
    toast.success("Blocked");
    onChanged?.();
    onClose();
  };
  const unblock = async () => {
    await api.post(`/admin/customers/${data.user_id}/unblock`);
    toast.success("Unblocked");
    onChanged?.();
    onClose();
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm grid place-items-center p-6"
      onClick={onClose}
      data-testid="customer-detail-modal"
    >
      <div
        className="bg-background border max-w-3xl w-full max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b flex justify-between items-start gap-4">
          <div>
            <p className="label-xs opacity-60">Customer</p>
            <h3 className="mt-2 text-3xl font-serif tracking-tighter">{data.name}</h3>
            <p className="mt-1 text-sm opacity-70">{data.email}</p>
            <p className="text-xs opacity-60">{data.phone || "—"}</p>
            <p className="text-xs opacity-60 mt-1">
              Joined {new Date(data.created_at).toLocaleString()} · {data.email_verified ? "Verified" : "Unverified"}
              {data.google_linked ? " · Google" : ""}
            </p>
            {data.is_blocked && (
              <p className="mt-2 label-xs border px-2 py-1 inline-block bg-foreground text-background">
                BLOCKED{data.blocked_reason ? ` — ${data.blocked_reason}` : ""}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {data.is_blocked ? (
              <Button onClick={unblock} className="rounded-none label-xs h-10" data-testid="detail-unblock">
                Unblock
              </Button>
            ) : (
              <Button onClick={block} className="rounded-none label-xs h-10" data-testid="detail-block">
                Block
              </Button>
            )}
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-none border-foreground label-xs h-10"
              data-testid="detail-close"
            >
              Close
            </Button>
          </div>
        </div>

        <div className="p-6">
          <p className="label-xs mb-3">Orders ({(data.orders || []).length})</p>
          {(!data.orders || data.orders.length === 0) && (
            <p className="opacity-60 text-sm py-8 text-center border border-dashed">No orders yet.</p>
          )}
          <div className="space-y-3">
            {(data.orders || []).map((o) => (
              <div key={o.id} className="border p-4 flex flex-wrap justify-between items-center gap-3">
                <div>
                  <p className="font-serif">{o.order_no}</p>
                  <p className="text-xs opacity-60">
                    {new Date(o.created_at).toLocaleString()} · {(o.items || []).length} items
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-body">{money(o.total)}</p>
                  <p className="label-xs opacity-60">{o.status}</p>
                </div>
              </div>
            ))}
          </div>

          {data.addresses?.length > 0 && (
            <>
              <p className="label-xs mt-8 mb-3">Addresses ({data.addresses.length})</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {data.addresses.map((a) => (
                  <div key={a.id} className="border p-4 text-sm">
                    <p className="font-serif">{a.full_name}</p>
                    <p className="opacity-70 text-xs">{a.phone}</p>
                    <p className="mt-2">
                      {a.line1}
                      {a.line2 ? `, ${a.line2}` : ""}
                      <br />
                      {a.city}, {a.state} {a.pincode}
                    </p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


/* ==================== Settings ==================== */
function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/settings").then(({ data }) => { setSettings(data); setDirty(false); });
  useEffect(() => { load(); }, []);

  const set = (patch) => { setSettings((s) => ({ ...s, ...patch })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/settings", settings || {});
      toast.success("Settings saved");
      setSettings((s) => ({ ...s, ...data }));
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <p className="opacity-60 label-xs text-center py-12">Loading…</p>;

  return (
    <div className="space-y-8" data-testid="settings-tab">
      <div className="flex justify-between items-center sticky top-20 bg-background/95 backdrop-blur border-b py-3 -mx-6 px-6 z-10">
        <p className="label-xs opacity-70">{dirty ? "Unsaved changes" : "Everything saved"}</p>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="rounded-none label-xs h-10 px-6 gap-2"
          data-testid="settings-save"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>

      <Section title="Brand">
        <FormRow>
          <FormField label="Store name"><Input value={settings.store_name || ""} onChange={(e) => set({ store_name: e.target.value })} className="rounded-none" data-testid="s-store-name" /></FormField>
          <FormField label="Tagline"><Input value={settings.tagline || ""} onChange={(e) => set({ tagline: e.target.value })} className="rounded-none" data-testid="s-tagline" /></FormField>
        </FormRow>
        <FormRow>
          <FormField label="Currency"><Input value={settings.currency || "INR"} onChange={(e) => set({ currency: e.target.value })} className="rounded-none" data-testid="s-currency" /></FormField>
          <FormField label="Currency symbol"><Input value={settings.currency_symbol || "₹"} onChange={(e) => set({ currency_symbol: e.target.value })} className="rounded-none" data-testid="s-currency-sym" /></FormField>
          <FormField label="Timezone"><Input value={settings.timezone || "Asia/Kolkata"} onChange={(e) => set({ timezone: e.target.value })} className="rounded-none" data-testid="s-tz" /></FormField>
        </FormRow>
        <FormField label="Desktop logo">
          <ImageUploader value={settings.logo_desktop} onChange={(v) => set({ logo_desktop: v })} kind="logo" aspect="aspect-[3/1]" testid="s-logo-desktop" label="Upload" />
        </FormField>
        <FormField label="Mobile logo">
          <ImageUploader value={settings.logo_mobile} onChange={(v) => set({ logo_mobile: v })} kind="logo" aspect="aspect-square" testid="s-logo-mobile" label="Upload" />
        </FormField>
      </Section>

      <Section title="Contact & Social">
        <FormRow>
          <FormField label="Support email"><Input value={settings.support_email || ""} onChange={(e) => set({ support_email: e.target.value })} className="rounded-none" data-testid="s-email" /></FormField>
          <FormField label="Support phone"><Input value={settings.support_phone || ""} onChange={(e) => set({ support_phone: e.target.value })} className="rounded-none" data-testid="s-phone" /></FormField>
        </FormRow>
        <FormRow>
          <FormField label="WhatsApp number (with country code, digits only)">
            <Input value={settings.whatsapp_number || ""} onChange={(e) => set({ whatsapp_number: e.target.value.replace(/\D/g, "") })} className="rounded-none" data-testid="s-wa-number" />
          </FormField>
          <FormField label="WhatsApp 'Need Help?' greeting">
            <Input value={settings.whatsapp_help_message || ""} onChange={(e) => set({ whatsapp_help_message: e.target.value })} className="rounded-none" data-testid="s-wa-msg" />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Instagram URL"><Input value={settings.instagram_url || ""} onChange={(e) => set({ instagram_url: e.target.value })} className="rounded-none" data-testid="s-insta" /></FormField>
          <FormField label="Facebook URL"><Input value={settings.facebook_url || ""} onChange={(e) => set({ facebook_url: e.target.value })} className="rounded-none" /></FormField>
          <FormField label="YouTube URL"><Input value={settings.youtube_url || ""} onChange={(e) => set({ youtube_url: e.target.value })} className="rounded-none" /></FormField>
        </FormRow>
      </Section>

      <Section title="Shipping">
        <FormRow>
          <FormField label="Enable shipping">
            <label className="flex items-center gap-3 h-10 select-none">
              <input type="checkbox" checked={!!settings.shipping_enabled} onChange={(e) => set({ shipping_enabled: e.target.checked })} className="h-4 w-4 accent-foreground" data-testid="s-ship-enabled" />
              <span className="text-sm">Charge shipping unless threshold met</span>
            </label>
          </FormField>
          <FormField label="Flat shipping rate (₹)">
            <Input type="number" value={settings.shipping_flat_rate ?? 0} onChange={(e) => set({ shipping_flat_rate: Number(e.target.value) })} className="rounded-none" data-testid="s-ship-flat" />
          </FormField>
          <FormField label="Free shipping threshold (₹)">
            <Input type="number" value={settings.free_shipping_threshold ?? 0} onChange={(e) => set({ free_shipping_threshold: Number(e.target.value) })} className="rounded-none" data-testid="s-ship-threshold" />
          </FormField>
        </FormRow>
        <FormField label="Estimated delivery">
          <Input value={settings.estimated_delivery || ""} onChange={(e) => set({ estimated_delivery: e.target.value })} className="rounded-none" data-testid="s-eta" />
        </FormField>
      </Section>

      <Section title="Payments (Razorpay)">
        <FormRow>
          <FormField label="Payment mode">
            <select
              value={settings.payment_mode || "advance_cod"}
              onChange={(e) => set({ payment_mode: e.target.value })}
              className="h-10 border border-input bg-background text-sm rounded-none px-3 w-full"
              data-testid="s-payment-mode"
            >
              <option value="online_only">100% Online Payment (Razorpay)</option>
              <option value="advance_cod">Configurable Advance (%) + Rest COD</option>
            </select>
            <p className="text-xs opacity-60 mt-2">
              Online Only charges customers the full amount at checkout. Advance splits between an online % and the balance in cash on delivery.
            </p>
          </FormField>
          <FormField label="Advance percentage (when mode = Advance)">
            <select
              value={String(settings.advance_percent ?? 30)}
              onChange={(e) => set({ advance_percent: Number(e.target.value) })}
              className="h-10 border border-input bg-background text-sm rounded-none px-3 w-full"
              data-testid="s-advance"
              disabled={settings.payment_mode === "online_only"}
            >
              <option value="10">10%</option>
              <option value="20">20%</option>
              <option value="30">30%</option>
              <option value="40">40%</option>
              <option value="50">50%</option>
              <option value="60">60%</option>
              <option value="70">70%</option>
              <option value="80">80%</option>
              <option value="90">90%</option>
            </select>
          </FormField>
        </FormRow>
        <FormField label="Enable Cash on Delivery for customers (hidden by default)">
          <label className="flex items-center gap-3 h-10 select-none">
            <input
              type="checkbox"
              checked={!!settings.allow_cod}
              onChange={(e) => set({ allow_cod: e.target.checked })}
              className="h-4 w-4 accent-foreground"
              data-testid="s-allow-cod"
            />
            <span className="text-sm">
              {settings.allow_cod
                ? "COD is visible on the customer checkout"
                : "COD is hidden — customers only see online payment"}
            </span>
          </label>
        </FormField>
        <FormRow>
          <FormField label="Razorpay Key ID (public — starts with rzp_live_ or rzp_test_)">
            <Input value={settings.razorpay_key_id || ""} onChange={(e) => set({ razorpay_key_id: e.target.value })} placeholder="rzp_live_XXXXXXXXXX" className="rounded-none" data-testid="s-rzp-key" />
          </FormField>
          <FormField label={`Razorpay Secret ${settings.razorpay_key_secret_configured ? "(saved · leave blank to keep)" : "(not set)"}`}>
            <Input type="password" onChange={(e) => set({ razorpay_key_secret: e.target.value })} placeholder="•••••••" className="rounded-none" data-testid="s-rzp-secret" />
          </FormField>
        </FormRow>
        <FormField label={`Razorpay webhook secret ${settings.razorpay_webhook_secret_configured ? "(saved · leave blank to keep)" : "(not set)"}`}>
          <Input type="password" onChange={(e) => set({ razorpay_webhook_secret: e.target.value })} placeholder="•••••••" className="rounded-none" data-testid="s-rzp-webhook" />
        </FormField>
      </Section>

      <Section title="Email (Resend)">
        <FormRow>
          <FormField label="Sender email">
            <Input value={settings.sender_email || ""} onChange={(e) => set({ sender_email: e.target.value })} className="rounded-none" data-testid="s-sender" />
          </FormField>
          <FormField label="Sender name">
            <Input value={settings.email_from_name || ""} onChange={(e) => set({ email_from_name: e.target.value })} className="rounded-none" />
          </FormField>
        </FormRow>
        <FormField label={`Resend API key ${settings.resend_api_key_configured ? "(saved · leave blank to keep)" : "(not set — emails disabled)"}`}>
          <Input type="password" onChange={(e) => set({ resend_api_key: e.target.value })} placeholder="re_•••••" className="rounded-none" data-testid="s-resend-key" />
        </FormField>
      </Section>

      <Section title="WhatsApp (Meta Cloud API — optional)">
        <p className="text-xs opacity-60 -mt-2">
          Configure to auto-send order confirmations via WhatsApp. Get credentials from{" "}
          <a href="https://business.facebook.com/wa/manage/home/" target="_blank" rel="noreferrer" className="link-underline text-foreground">
            Meta Business → WhatsApp Manager
          </a>. Leave disabled to skip WhatsApp entirely.
        </p>
        <FormRow>
          <FormField label="Enable WhatsApp confirmations">
            <label className="flex items-center gap-3 h-10 select-none">
              <input
                type="checkbox"
                checked={!!settings.whatsapp_enabled}
                onChange={(e) => set({ whatsapp_enabled: e.target.checked })}
                className="h-4 w-4 accent-foreground"
                data-testid="s-wa-enabled"
              />
              <span className="text-sm">
                {settings.whatsapp_enabled ? "Enabled — orders trigger a WhatsApp message" : "Disabled (default)"}
              </span>
            </label>
          </FormField>
          <FormField label="Phone Number ID (Meta)">
            <Input
              value={settings.whatsapp_phone_number_id || ""}
              onChange={(e) => set({ whatsapp_phone_number_id: e.target.value })}
              placeholder="e.g. 123456789012345"
              className="rounded-none"
              data-testid="s-wa-phone-id"
            />
          </FormField>
        </FormRow>
        <FormField label={`Permanent Access Token ${settings.whatsapp_access_token_configured ? "(saved · leave blank to keep)" : "(not set)"}`}>
          <Input
            type="password"
            onChange={(e) => set({ whatsapp_access_token: e.target.value })}
            placeholder="EAAG•••••"
            className="rounded-none"
            data-testid="s-wa-token"
          />
        </FormField>
        <FormField label="Confirmation message template">
          <Textarea
            rows={8}
            value={settings.whatsapp_confirmation_template || ""}
            onChange={(e) => set({ whatsapp_confirmation_template: e.target.value })}
            className="rounded-none font-mono text-xs"
            data-testid="s-wa-template"
          />
          <p className="text-[11px] opacity-60 mt-1">
            Placeholders: <code>{"{customer_name} {brand} {order_id} {order_items} {total} {payment_status} {delivery_eta}"}</code>
          </p>
        </FormField>
      </Section>

      <Section title="SEO & Analytics">
        <FormRow>
          <FormField label="Meta title"><Input value={settings.seo_title || ""} onChange={(e) => set({ seo_title: e.target.value })} className="rounded-none" data-testid="s-seo-title" /></FormField>
          <FormField label="Canonical domain (https://…)"><Input value={settings.canonical_domain || ""} onChange={(e) => set({ canonical_domain: e.target.value })} className="rounded-none" data-testid="s-canonical" /></FormField>
        </FormRow>
        <FormField label="Meta description">
          <Textarea rows={2} value={settings.seo_description || ""} onChange={(e) => set({ seo_description: e.target.value })} className="rounded-none" data-testid="s-seo-desc" />
        </FormField>
        <FormField label="Meta keywords (comma-separated)"><Input value={settings.seo_keywords || ""} onChange={(e) => set({ seo_keywords: e.target.value })} className="rounded-none" /></FormField>
        <FormField label="Open Graph image URL"><Input value={settings.og_image || ""} onChange={(e) => set({ og_image: e.target.value })} className="rounded-none" /></FormField>
        <FormRow>
          <FormField label="Google Analytics ID (G-XXXX)"><Input value={settings.google_analytics_id || ""} onChange={(e) => set({ google_analytics_id: e.target.value })} className="rounded-none" data-testid="s-ga" /></FormField>
          <FormField label="Meta Pixel ID"><Input value={settings.meta_pixel_id || ""} onChange={(e) => set({ meta_pixel_id: e.target.value })} className="rounded-none" data-testid="s-pixel" /></FormField>
        </FormRow>
      </Section>

      <Section title="Business (GST / Legal)">
        <FormRow>
          <FormField label="GST number"><Input value={settings.gst_number || ""} onChange={(e) => set({ gst_number: e.target.value })} className="rounded-none" data-testid="s-gst" /></FormField>
        </FormRow>
        <FormField label="Business address (shown on invoice)">
          <Textarea rows={3} value={settings.business_address || ""} onChange={(e) => set({ business_address: e.target.value })} className="rounded-none" />
        </FormField>
      </Section>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border p-6 space-y-4">
      <p className="label-xs">{title}</p>
      {children}
    </div>
  );
}
function FormRow({ children }) { return <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{children}</div>; }
function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="label-xs opacity-60">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

/* ==================== Coupons ==================== */
function CouponsTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", type: "percent", value: 10, min_amount: 0, usage_limit: "", per_user_limit: 1, expires_at: "", is_active: true });
  const [busy, setBusy] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const load = () => api.get("/admin/coupons").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return toast.error("Code required");
    setBusy(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        value: Number(form.value),
        min_amount: Number(form.min_amount || 0),
        usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
        per_user_limit: form.per_user_limit ? Number(form.per_user_limit) : null,
        expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : null,
      };
      await api.post("/admin/coupons", payload);
      toast.success("Coupon created");
      setForm({ code: "", type: "percent", value: 10, min_amount: 0, usage_limit: "", per_user_limit: 1, expires_at: "", is_active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (c) => { await api.patch(`/admin/coupons/${c.code}`, { is_active: !c.is_active }); load(); };
  const del = async (c) => { if (!confirm(`Delete coupon ${c.code}?`)) return; await api.delete(`/admin/coupons/${c.code}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-[380px_1fr] gap-8" data-testid="coupons-tab">
      <form onSubmit={create} className="border p-6 space-y-4 h-fit" data-testid="coupon-form">
        <p className="label-xs">New Coupon</p>
        <FormField label="Code"><Input required value={form.code} onChange={(e) => set({ code: e.target.value.toUpperCase() })} className="rounded-none uppercase" data-testid="cpf-code" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Type">
            <Select value={form.type} onValueChange={(v) => set({ type: v })}>
              <SelectTrigger className="rounded-none" data-testid="cpf-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percent">Percent</SelectItem>
                <SelectItem value="flat">Flat</SelectItem>
              </SelectContent>
            </Select>
          </FormField>
          <FormField label={form.type === "percent" ? "Value (%)" : "Value (₹)"}><Input required type="number" min="0" value={form.value} onChange={(e) => set({ value: e.target.value })} className="rounded-none" data-testid="cpf-value" /></FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Min order (₹)"><Input type="number" min="0" value={form.min_amount} onChange={(e) => set({ min_amount: e.target.value })} className="rounded-none" data-testid="cpf-min" /></FormField>
          <FormField label="Usage limit"><Input type="number" min="0" value={form.usage_limit} onChange={(e) => set({ usage_limit: e.target.value })} className="rounded-none" placeholder="Unlimited" data-testid="cpf-limit" /></FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Per-user limit"><Input type="number" min="0" value={form.per_user_limit} onChange={(e) => set({ per_user_limit: e.target.value })} className="rounded-none" data-testid="cpf-per-user" /></FormField>
          <FormField label="Expires"><Input type="datetime-local" value={form.expires_at} onChange={(e) => set({ expires_at: e.target.value })} className="rounded-none" data-testid="cpf-expires" /></FormField>
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-none label-xs h-11" data-testid="cpf-submit">
          {busy ? "Saving…" : "Create Coupon"}
        </Button>
      </form>
      <div className="space-y-3">
        {rows.length === 0 && <p className="opacity-60 py-16 text-center border border-dashed">No coupons yet.</p>}
        {rows.map((c) => (
          <div key={c.code} className="border p-4 flex flex-wrap items-center gap-4" data-testid={`coupon-row-${c.code}`}>
            <div className="flex-1 min-w-[180px]">
              <p className="font-serif text-lg">{c.code}</p>
              <p className="text-xs opacity-60 mt-0.5">
                {c.type === "percent" ? `${c.value}% off` : `₹${c.value} off`} · Min ₹{c.min_amount || 0}
                {c.usage_limit ? ` · ${c.used_count || 0}/${c.usage_limit} used` : ` · ${c.used_count || 0} used`}
                {c.expires_at ? ` · until ${new Date(c.expires_at).toLocaleDateString()}` : ""}
              </p>
            </div>
            <button
              onClick={() => toggle(c)}
              className="h-9 px-3 border label-xs flex items-center gap-2 hover:bg-foreground hover:text-background"
              style={{ transitionProperty: "background-color, color" }}
              data-testid={`coupon-toggle-${c.code}`}
            >
              {c.is_active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              {c.is_active ? "Active" : "Inactive"}
            </button>
            <button onClick={() => del(c)} className="h-9 w-9 border grid place-items-center" aria-label="Delete" data-testid={`coupon-del-${c.code}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================== Reviews ==================== */
function ReviewsTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => {
    const q = filter === "all" ? "" : `?approved=${filter === "approved"}`;
    api.get(`/admin/reviews${q}`).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); }, [filter]);
  const approve = async (r, v) => { await api.patch(`/admin/reviews/${r.id}/approve`, { is_approved: v }); toast.success(v ? "Approved" : "Hidden"); load(); };
  const del = async (r) => { if (!confirm("Delete review?")) return; await api.delete(`/admin/reviews/${r.id}`); toast.success("Deleted"); load(); };
  return (
    <div data-testid="reviews-tab">
      <div className="flex items-center justify-between mb-6">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-44 rounded-none" data-testid="reviews-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <p className="label-xs opacity-60">{rows.length} total</p>
      </div>
      {rows.length === 0 && <p className="opacity-60 py-16 text-center">No reviews here.</p>}
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.id} className="border p-5" data-testid={`review-row-${r.id}`}>
            <div className="flex flex-wrap justify-between items-start gap-3">
              <div>
                <p className="font-serif text-lg">{r.author}</p>
                <p className="text-xs opacity-60 mt-0.5">Product {r.product_id} · {r.rating}★ · {new Date(r.created_at).toLocaleDateString()}</p>
              </div>
              <div className="flex gap-2">
                {!r.is_approved ? (
                  <Button onClick={() => approve(r, true)} className="rounded-none label-xs h-9" data-testid={`review-approve-${r.id}`}>Approve</Button>
                ) : (
                  <Button variant="outline" onClick={() => approve(r, false)} className="rounded-none border-foreground label-xs h-9" data-testid={`review-hide-${r.id}`}>Hide</Button>
                )}
                <button onClick={() => del(r)} className="h-9 w-9 border grid place-items-center" aria-label="Delete" data-testid={`review-del-${r.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <p className="mt-3 text-sm opacity-80 leading-relaxed">{r.comment}</p>
            {(r.images || []).length > 0 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {r.images.map((im, i) => (
                  <img key={i} src={im} alt="" className="w-20 h-20 object-cover border" />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ==================== Homepage Media (video hero / brand film / lookbook) ==================== */
const MEDIA_KINDS = [
  { value: "hero_video", label: "Hero (video)" },
  { value: "brand_film", label: "Brand Film" },
  { value: "lookbook", label: "Lookbook Image" },
  { value: "banner", label: "Simple Banner" },
];
const EMPTY_MEDIA = {
  kind: "brand_film", title: "", subtitle: "", eyebrow: "", cta_label: "", cta_link: "",
  desktop_video: "", mobile_video: "", desktop_image: "", mobile_image: "",
  is_active: true, order: 1,
};
function HomepageMediaTab() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_MEDIA);
  const set = (p) => setForm((f) => ({ ...f, ...p }));

  const load = () => api.get("/admin/homepage-media").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.desktop_video && !form.desktop_image) return toast.error("Add either a video or an image");
    try {
      await api.post("/admin/homepage-media", { ...form, order: Number(form.order) || rows.length + 1 });
      toast.success("Media added");
      setForm(EMPTY_MEDIA);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const patch = async (id, body) => { await api.patch(`/admin/homepage-media/${id}`, body); load(); };
  const del = async (id) => { if (!confirm("Delete this media?")) return; await api.delete(`/admin/homepage-media/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="grid lg:grid-cols-[420px_1fr] gap-8" data-testid="homepage-tab">
      <form onSubmit={create} className="border p-6 space-y-4 h-fit" data-testid="homepage-form">
        <p className="label-xs">New Homepage Media</p>
        <FormField label="Kind">
          <Select value={form.kind} onValueChange={(v) => set({ kind: v })}>
            <SelectTrigger className="rounded-none" data-testid="hp-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MEDIA_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Desktop image (poster / cover)">
          <ImageUploader value={form.desktop_image} onChange={(v) => set({ desktop_image: v })} kind="homepage" aspect="aspect-[16/9]" testid="hp-desk-img" />
        </FormField>
        <FormField label="Mobile image (portrait)">
          <ImageUploader value={form.mobile_image} onChange={(v) => set({ mobile_image: v })} kind="homepage-m" aspect="aspect-[9/16]" testid="hp-mob-img" />
        </FormField>
        <FormField label="Desktop video URL (mp4/webm)"><Input value={form.desktop_video} onChange={(e) => set({ desktop_video: e.target.value })} className="rounded-none" placeholder="https://…mp4" data-testid="hp-desk-video" /></FormField>
        <FormField label="Mobile video URL (optional)"><Input value={form.mobile_video} onChange={(e) => set({ mobile_video: e.target.value })} className="rounded-none" placeholder="https://…mp4" data-testid="hp-mob-video" /></FormField>
        <FormField label="Eyebrow"><Input value={form.eyebrow} onChange={(e) => set({ eyebrow: e.target.value })} className="rounded-none" /></FormField>
        <FormField label="Title"><Input value={form.title} onChange={(e) => set({ title: e.target.value })} className="rounded-none" data-testid="hp-title" /></FormField>
        <FormField label="Subtitle"><Textarea rows={2} value={form.subtitle} onChange={(e) => set({ subtitle: e.target.value })} className="rounded-none" /></FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="CTA label"><Input value={form.cta_label} onChange={(e) => set({ cta_label: e.target.value })} className="rounded-none" /></FormField>
          <FormField label="CTA link"><Input value={form.cta_link} onChange={(e) => set({ cta_link: e.target.value })} className="rounded-none" placeholder="/shop" /></FormField>
        </div>
        <FormField label="Order"><Input type="number" value={form.order} onChange={(e) => set({ order: e.target.value })} className="rounded-none" /></FormField>
        <label className="flex items-center gap-3 select-none">
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => set({ is_active: e.target.checked })} className="h-4 w-4 accent-foreground" />
          <span className="text-sm">Active</span>
        </label>
        <Button type="submit" className="w-full rounded-none label-xs h-11" data-testid="hp-submit">Save Media</Button>
      </form>

      <div className="space-y-3">
        {rows.length === 0 && <p className="opacity-60 py-16 text-center border border-dashed">No homepage media yet.</p>}
        {rows.map((m) => (
          <div key={m.id} className="border p-4 flex flex-wrap gap-4 items-center" data-testid={`hp-row-${m.id}`}>
            <div className="w-32 aspect-[16/9] bg-muted overflow-hidden">
              {m.desktop_image && <img src={m.desktop_image} alt="" className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="label-xs opacity-60">{MEDIA_KINDS.find((k) => k.value === m.kind)?.label || m.kind}</p>
              <p className="font-serif text-lg mt-1">{m.title || <span className="opacity-40">Untitled</span>}</p>
              <p className="text-xs opacity-60 mt-1">Order {m.order} · {m.is_active ? "Active" : "Hidden"}{m.desktop_video ? " · Video" : ""}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => patch(m.id, { is_active: !m.is_active })} className="h-9 px-3 border label-xs flex items-center gap-2" data-testid={`hp-toggle-${m.id}`}>
                {m.is_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {m.is_active ? "Hide" : "Show"}
              </button>
              <button onClick={() => del(m.id)} className="h-9 w-9 border grid place-items-center" aria-label="Delete" data-testid={`hp-del-${m.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

